import type { OfferVatMode } from '../../../types';

export type OfferVatOption = {
  mode: OfferVatMode;
  label: string;
  ratePercent: number;
  legalNote?: string;
};

export const OFFER_VAT_OPTIONS: OfferVatOption[] = [
  { mode: 'vat_19', label: '19 %', ratePercent: 19 },
  { mode: 'vat_16', label: '16 %', ratePercent: 16 },
  { mode: 'vat_7', label: '7 %', ratePercent: 7 },
  { mode: 'vat_5', label: '5 %', ratePercent: 5 },
  {
    mode: 'vat_0_innergemeinschaftliche_lieferung',
    label: '0 % Innergemeinschaftliche Lieferung (§4 Nr. 1b UStG i.V.m. §6a UStG)',
    ratePercent: 0,
    legalNote:
      'Gemäß § 4 Nr. 1b UStG i.V.m. § 6a UStG wird keine Umsatzsteuer auf innergemeinschaftliche Lieferungen ausgewiesen.',
  },
  {
    mode: 'vat_0_organschaft',
    label: '0 % Organschaft (§2 Abs. 2 Nr. 2 UStG)',
    ratePercent: 0,
    legalNote:
      'Gemäß § 2 Abs. 2 Nr. 2 UStG erfolgt diese Verrechnung im Rahmen einer umsatzsteuerlichen Organschaft. Es wird keine Umsatzsteuer ausgewiesen.',
  },
  {
    mode: 'vat_0_photovoltaik',
    label: '0 % Photovoltaikanlagen (§12 Abs. 3 Nr. 1 UStG)',
    ratePercent: 0,
    legalNote:
      'Gemäß § 12 Abs. 3 Nr. 1 UStG wird keine Umsatzsteuer auf Photovoltaikanlagen ausgewiesen.',
  },
  {
    mode: 'vat_0_reverse_charge',
    label: '0 % Reverse-Charge (§13b UStG)',
    ratePercent: 0,
    legalNote:
      'Gemäß § 13b UStG gilt die Steuerschuldnerschaft des Leistungsempfängers (Reverse-Charge). Es wird keine Umsatzsteuer ausgewiesen.',
  },
  {
    mode: 'vat_0_kleinunternehmer',
    label: '0 % Kleinunternehmer (§19 UStG)',
    ratePercent: 0,
    legalNote: 'Gemäß § 19 UStG wird keine Umsatzsteuer ausgewiesen.',
  },
  {
    mode: 'vat_0_grundstuecksverkauf',
    label: '0 % Grundstücksverkauf (§4 Nr. 9a UStG)',
    ratePercent: 0,
    legalNote: 'Gemäß § 4 Nr. 9a UStG wird keine Umsatzsteuer auf Grundstücksverkäufe ausgewiesen.',
  },
];

export const DEFAULT_OFFER_VAT_MODE: OfferVatMode = 'vat_19';

export function isOfferVatMode(value: unknown): value is OfferVatMode {
  if (typeof value !== 'string') return false;
  return OFFER_VAT_OPTIONS.some((opt) => opt.mode === value);
}

export function getOfferVatOption(mode: OfferVatMode | null | undefined): OfferVatOption {
  if (mode) {
    const found = OFFER_VAT_OPTIONS.find((opt) => opt.mode === mode);
    if (found) return found;
  }
  return OFFER_VAT_OPTIONS[0];
}

export function isZeroVatSpecialMode(mode: OfferVatMode | null | undefined): boolean {
  const option = getOfferVatOption(mode ?? DEFAULT_OFFER_VAT_MODE);
  return option.ratePercent === 0 && Boolean(option.legalNote);
}

/** Reverse charge (AE) and intra-community supply (K) require the buyer's USt-IdNr. */
export function vatModeRequiresBuyerVatId(mode: OfferVatMode | string | null | undefined): boolean {
  return mode === 'vat_0_reverse_charge' || mode === 'vat_0_innergemeinschaftliche_lieferung';
}
